import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import HeroSection from "../../components/feature/sections/HeroSection";
import TrustBar from "../../components/feature/sections/TrustBar";
import BrandsBar from "../../components/feature/sections/BrandsBar";
import HomeServicesGrid from "./components/HomeServicesGrid";
import AIAnswerBlock from "../../components/feature/sections/AIAnswerBlock";
import ProcessSection from "../../components/feature/sections/ProcessSection";
import WhyChooseUs from "../../components/feature/sections/WhyChooseUs";
import ServiceAreasSection from "../../components/feature/sections/ServiceAreasSection";
import TestimonialsSection from "../../components/feature/sections/TestimonialsSection";
import FAQSection from "../../components/feature/sections/FAQSection";
import FinalCTA from "../../components/feature/sections/FinalCTA";
import LeadFormSection from "../../components/feature/sections/LeadFormSection";
import DuctlessSection from "./components/DuctlessSection";
import { PROCESS_STEPS, WHY_FEATURES, HOME_FAQS } from "../../mocks/siteData";
import homeContent from "@/content/home.json";
import { useRef, useEffect } from "react";

const scroll = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

function useReveal(ref: React.RefObject<HTMLElement | null>, delay = 0) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setTimeout(() => { el.style.opacity = "1"; el.style.transform = "translateY(0)"; }, delay);
        obs.disconnect();
      }
    }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [delay, ref]);
}

export default function Home() {
  const postSvcRef = useRef<HTMLDivElement>(null);
  const postTestiRef = useRef<HTMLDivElement>(null);
  const postServicesCta = homeContent.inlineCtas.afterServices;
  const postTestimonialsCta = homeContent.inlineCtas.afterTestimonials;
  useReveal(postSvcRef as React.RefObject<HTMLElement>, 0);
  useReveal(postTestiRef as React.RefObject<HTMLElement>, 0);

  return (
    <>
      <Navbar />
      <main>
        <HeroSection
          eyebrow={homeContent.hero.eyebrow}
          headline={homeContent.hero.headline}
          subheadline={homeContent.hero.subheadline}
          urgencyLine={homeContent.hero.urgencyLine}
          primaryCTA={homeContent.hero.primaryCTA}
          secondaryCTA={homeContent.hero.secondaryCTA}
        />
        <TrustBar />
        <BrandsBar />
        <HomeServicesGrid />
        <DuctlessSection />

        {/* POST-SERVICES CTA */}
        <div ref={postSvcRef} className="relative overflow-hidden" style={{ opacity: 0, transform: "translateY(20px)", transition: "opacity 0.9s cubic-bezier(0.4,0,0.2,1), transform 0.9s cubic-bezier(0.4,0,0.2,1)" }}>
          <div className="absolute inset-0">
            {postServicesCta.backgroundImage && <img src={postServicesCta.backgroundImage} alt="Premium home comfort by Park Lawn Heating" className="w-full h-full object-cover object-center" />}
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/97 via-[#0A0A0A]/85 to-[#0A0A0A]/60" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-[#0A0A0A]/40" />
          <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 lg:py-28 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-10">
            <div className="max-w-xl">
              <div className="flex items-center gap-3 mb-5">
                <span className="w-8 h-px bg-[#C4902A]" />
                <p className="text-[#C4902A] text-[11px] font-bold tracking-[0.3em] uppercase">{postServicesCta.eyebrow}</p>
              </div>
              <h3 className="text-white font-serif leading-tight mb-4" style={{ fontSize: "clamp(1.7rem, 3vw, 2.5rem)", fontFamily: "'Playfair Display', Georgia, serif" }}>
                {postServicesCta.headline}
              </h3>
              <p className="text-white/60 text-sm leading-relaxed mb-3">{postServicesCta.description}</p>
              <div className="flex items-center gap-2.5 mt-1">
                <div className="w-1.5 h-1.5 rounded-full bg-[#C4902A] shrink-0 animate-pulse" />
                <p className="text-[#C4902A] text-xs font-bold tracking-wide uppercase">{postServicesCta.urgency}</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 shrink-0">
              <a href={postServicesCta.primaryCTA.href} onClick={(e) => { e.preventDefault(); scroll(postServicesCta.primaryCTA.href.replace("#", "")); }} className="inline-flex items-center justify-center gap-2.5 bg-[#C4902A] text-white font-bold px-9 py-4 rounded-full hover:bg-[#d4a030] transition-all whitespace-nowrap text-sm tracking-wide shadow-xl shadow-[#C4902A]/30 cursor-pointer">
                {postServicesCta.primaryCTA.label} <i className="ri-arrow-right-line"></i>
              </a>
              <a href={postServicesCta.secondaryCTA.href} className="inline-flex items-center justify-center gap-2 border border-white/20 text-white font-semibold px-9 py-4 rounded-full hover:bg-white/[0.07] hover:border-white/40 transition-all whitespace-nowrap text-sm">
                <i className="ri-phone-fill text-[#C4902A]"></i> {postServicesCta.secondaryCTA.label}
              </a>
            </div>
          </div>
        </div>

        <AIAnswerBlock
          headline={homeContent.aiAnswer.headline}
          intro={homeContent.aiAnswer.intro}
          whatWeDo={homeContent.aiAnswer.whatWeDo}
          services={homeContent.aiAnswer.services}
          benefits={homeContent.aiAnswer.benefits}
          area={homeContent.aiAnswer.area}
        />
        <ProcessSection headline={homeContent.process.headline} subheadline={homeContent.process.subheadline} steps={PROCESS_STEPS} />
        <WhyChooseUs headline={homeContent.whyChooseUs.headline} intro={homeContent.whyChooseUs.intro} features={WHY_FEATURES} />
        <ServiceAreasSection />
        <TestimonialsSection />

        {/* POST-TESTIMONIALS CTA */}
        <div ref={postTestiRef} className="relative overflow-hidden py-20" style={{ background: "linear-gradient(135deg, #0D0D0D 0%, #1A160A 100%)", opacity: 0, transform: "translateY(16px)", transition: "opacity 0.9s cubic-bezier(0.4,0,0.2,1), transform 0.9s cubic-bezier(0.4,0,0.2,1)" }}>
          <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 60% 60% at 50% 50%, rgba(196,144,42,0.07) 0%, transparent 65%)" }} />
          <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
            <div className="flex gap-0.5 justify-center mb-5">
              {[1,2,3,4,5].map(i => <i key={i} className="ri-star-fill text-[#C4902A] text-lg"></i>)}
            </div>
            <p className="text-white font-serif mb-2 leading-tight" style={{ fontSize: "clamp(1.4rem, 2.5vw, 1.9rem)", fontFamily: "'Playfair Display', Georgia, serif" }}>
              {postTestimonialsCta.headline}
            </p>
            <p className="text-white/40 text-sm mb-2">{postTestimonialsCta.description}</p>
            <div className="flex items-center justify-center gap-2.5 mb-8">
              <div className="w-1.5 h-1.5 rounded-full bg-[#C4902A] shrink-0 animate-pulse" />
              <p className="text-[#C4902A] text-xs font-bold tracking-wide uppercase">{postTestimonialsCta.urgency}</p>
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href={postTestimonialsCta.primaryCTA.href} onClick={(e) => { e.preventDefault(); scroll(postTestimonialsCta.primaryCTA.href.replace("#", "")); }} className="inline-flex items-center gap-2.5 bg-[#C4902A] text-white font-bold px-10 py-4 rounded-full hover:bg-[#d4a030] transition-all whitespace-nowrap text-sm tracking-wide shadow-xl shadow-[#C4902A]/25 cursor-pointer">
                {postTestimonialsCta.primaryCTA.label} <i className="ri-arrow-right-line"></i>
              </a>
              <a href={postTestimonialsCta.secondaryCTA.href} className="inline-flex items-center gap-2.5 border border-white/20 text-white font-semibold px-10 py-4 rounded-full hover:bg-white/[0.06] hover:border-white/40 transition-all whitespace-nowrap text-sm">
                <i className="ri-phone-fill text-[#C4902A]"></i> {postTestimonialsCta.secondaryCTA.label}
              </a>
            </div>
          </div>
        </div>

        <FAQSection faqs={HOME_FAQS} />
        <LeadFormSection />
        <FinalCTA
          headline={homeContent.finalCTA.headline}
          subheadline={homeContent.finalCTA.subheadline}
          ctaLabel={homeContent.finalCTA.ctaLabel}
          ctaHref={homeContent.finalCTA.ctaHref}
        />
      </main>
      <Footer />
    </>
  );
}
